import {
  ASSESSMENT_DEFINITIONS,
  getAssessmentRecordsByType,
  getAssessmentTypeFromRecord,
} from "../assessmentConfig";
import type { AssessmentRecord, AssessmentData, AssessmentType } from "../types";
import {
  getPeakAgeRange,
  getTypicalAgeScore,
  getDifferenceInScore,
  getFoundationalLearningAgeRange,
  getPerceptualLearningAgeRange,
  getVerbalComprehensionAgeRange,
  getVerbalReasoningMemoryAgeRange,
  getFoundationalLearningBasicSocialAgeRange,
  getAdvancedVerbalComprehensionAdvancedMathAgeRange,
  getVerbalReasoningAdvancedReadingWritingAgeRange,
  getDirectTeachingAgeRange,
  getGeneralizationAgeRange,
  sumScores,
} from "../util/peakCalculations";

// ─── v3 API Normalization ─────────────────────────────────────────────────

function camelToSnake(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

function toSnakeCaseData(obj: Record<string, unknown>): Partial<AssessmentData> {
  return Object.fromEntries(
    Object.entries(obj).map(([key, val]) => [camelToSnake(key), val])
  ) as Partial<AssessmentData>;
}

/**
 * Normalizes a flat camelCase v3 API assessment object into the AssessmentRecord
 * shape consumed by the UI. Fields are converted from camelCase to snake_case and
 * placed in the nested `data` object.
 *
 * @param rawItems - Raw objects returned by the v3 assessments endpoint
 * @param assessmentType - The assessment type label to attach to each record
 */
export function normalizeV3Records(
  rawItems: unknown[],
  assessmentType: AssessmentType
): AssessmentRecord[] {
  return rawItems.map((raw) => {
    const item = raw as Record<string, unknown>;
    const {
      id,
      clientId,
      createdAt,
      updatedAt,
      administratorName,
      batchId: _batchId,
      ...rest
    } = item;

    const snakeData = toSnakeCaseData(rest as Record<string, unknown>);
    // CPFQ uses child_administration_date instead of administration_date
    const administrationDate =
      (snakeData.administration_date as string | undefined) ??
      (snakeData.child_administration_date as string | undefined) ??
      (createdAt as string) ??
      "";

    return {
      id: id as string | number | undefined,
      client_id: (clientId as string) ?? "",
      evaluation_date: administrationDate,
      created: (createdAt as string) ?? "",
      last_updated: (updatedAt as string) ?? "",
      created_by: (administratorName as string) ?? "",
      last_updated_by: (administratorName as string) ?? "",
      assessment_type: assessmentType,
      type: assessmentType,
      assessment_name: assessmentType,
      data: {
        ...snakeData,
        administration_date: administrationDate,
        assessment_type: assessmentType,
        type: assessmentType,
      } as AssessmentData,
    };
  });
}

const DEFAULT_MIN_HISTORY_POINTS_BY_TYPE: Partial<Record<AssessmentType, number>> = {
  vineland: 4,
};

export function normalizeAssessmentRecords(records: AssessmentRecord[]): AssessmentRecord[] {
  return records.map((record) => {
    const assessmentType = getAssessmentTypeFromRecord(record);

    return {
      ...record,
      assessment_type: assessmentType,
      type: assessmentType,
      data: {
        ...record.data,
        assessment_type: assessmentType,
        type: assessmentType,
      },
    };
  });
}

function sortByAdministrationDate(records: AssessmentRecord[]): AssessmentRecord[] {
  return [...records].sort((a, b) => {
    const aDate = new Date(a.data.administration_date).getTime();
    const bDate = new Date(b.data.administration_date).getTime();
    return aDate - bDate;
  });
}

export function mergeWithMockFloor(
  apiRecords: AssessmentRecord[],
  mockRecords: AssessmentRecord[],
  minimumHistoryPoints?: number
): AssessmentRecord[] {
  if (!apiRecords.length) {
    return sortByAdministrationDate(mockRecords);
  }

  if (!minimumHistoryPoints || apiRecords.length >= minimumHistoryPoints) {
    return sortByAdministrationDate(apiRecords);
  }

  const recordsByDate = new Map<string, AssessmentRecord>();

  mockRecords.forEach((record) => {
    recordsByDate.set(record.data.administration_date, record);
  });

  apiRecords.forEach((record) => {
    recordsByDate.set(record.data.administration_date, record);
  });

  return sortByAdministrationDate(Array.from(recordsByDate.values()));
}

export function mergeAssessmentsWithMockFloor(
  apiAssessments: AssessmentRecord[],
  mockAssessments: AssessmentRecord[],
  minimumHistoryPointsByType: Partial<
    Record<AssessmentType, number>
  > = DEFAULT_MIN_HISTORY_POINTS_BY_TYPE
): AssessmentRecord[] {
  const apiByType = getAssessmentRecordsByType(apiAssessments);
  const mockByType = getAssessmentRecordsByType(mockAssessments);

  return ASSESSMENT_DEFINITIONS.flatMap((definition) => {
    return mergeWithMockFloor(
      apiByType[definition.type],
      mockByType[definition.type],
      minimumHistoryPointsByType[definition.type]
    );
  });
}

// ─── Derived Field Computation ────────────────────────────────────────────

const VBMAPP_MILESTONES_SCORE_KEYS: Array<keyof AssessmentData> = [
  "mand",
  "tact",
  "listener_responding",
  "visual_perceptual_mts",
  "play",
  "social",
  "motor_imitation",
  "echoic",
  "vocal",
  "listener_responding_lrffc",
  "intraverbal",
  "group",
  "linguistics",
  "reading",
  "writing",
  "math",
];

function toNum(data: AssessmentData, key: string): number | null {
  const v = data[key];
  return typeof v === "number" ? v : null;
}

function applyVbmappMilestoneDerived(record: AssessmentRecord): AssessmentRecord {
  // Only compute if total_score is missing (may already exist from backend).
  if (record.data.total_score != null) return record;

  const scores = VBMAPP_MILESTONES_SCORE_KEYS.map((k) => toNum(record.data, k as string));
  const total = sumScores(scores);
  if (total === null) return record;

  return { ...record, data: { ...record.data, total_score: total } };
}

function applyPeakDerived(record: AssessmentRecord, ageInYears: number | null): AssessmentRecord {
  const d = record.data;
  const ageRange = getPeakAgeRange(ageInYears);

  // ── DT subscales ──────────────────────────────────────────────────────
  const fl = toNum(d, "dt_foundational_learning_score");
  const flTypical = getTypicalAgeScore("foundationalLearning", ageRange);
  const flAgeRange = getFoundationalLearningAgeRange(fl);
  const flDiff = getDifferenceInScore(flTypical, fl);

  const pl = toNum(d, "dt_perceptual_learning_score");
  const plTypical = getTypicalAgeScore("perceptualLearning", ageRange);
  const plAgeRange = getPerceptualLearningAgeRange(pl);
  const plDiff = getDifferenceInScore(plTypical, pl);

  const vc = toNum(d, "dt_verbal_comprehension_score");
  const vcTypical = getTypicalAgeScore("verbalComprehension", ageRange);
  const vcAgeRange = getVerbalComprehensionAgeRange(vc);
  const vcDiff = getDifferenceInScore(vcTypical, vc);

  const vrm = toNum(d, "dt_verbal_reasoning_memory_score");
  const vrmTypical = getTypicalAgeScore("verbalReasoningMemory", ageRange);
  const vrmAgeRange = getVerbalReasoningMemoryAgeRange(vrm);
  const vrmDiff = getDifferenceInScore(vrmTypical, vrm);

  const dtTotal = sumScores([fl, pl, vc, vrm]);
  const dtTypical = getTypicalAgeScore("directTeachingTotal", ageRange);
  const dtAgeRange = getDirectTeachingAgeRange(dtTotal);
  const dtDiff = getDifferenceInScore(dtTypical, dtTotal);

  // ── GEN subscales ─────────────────────────────────────────────────────
  const flBs = toNum(d, "g_foundational_learning_basic_social_score");
  const flBsTypical = getTypicalAgeScore("foundationalLearningBasicSocialSkills", ageRange);
  const flBsAgeRange = getFoundationalLearningBasicSocialAgeRange(flBs);
  const flBsDiff = getDifferenceInScore(flBsTypical, flBs);

  const bv = toNum(d, "g_basic_verbal_comprehension_advanced_social_score");
  const bvTypical = getTypicalAgeScore("basicVerbalComprehensionAdvancedSocialSkills", ageRange);
  const bvDiff = getDifferenceInScore(bvTypical, bv);

  const av = toNum(d, "g_advanced_verbal_comprehension_problem_solving_score");
  const avTypical = getTypicalAgeScore("advancedVerbalComprehensionAdvancedMathSkills", ageRange);
  const avAgeRange = getAdvancedVerbalComprehensionAdvancedMathAgeRange(av);
  const avDiff = getDifferenceInScore(avTypical, av);

  const vr = toNum(d, "g_verbal_reasoning_advanced_problem_solving_score");
  const vrTypical = getTypicalAgeScore("verbalReasoningAdvancedReadingWritingSkills", ageRange);
  const vrAgeRange = getVerbalReasoningAdvancedReadingWritingAgeRange(vr);
  const vrDiff = getDifferenceInScore(vrTypical, vr);

  const genTotal = sumScores([flBs, bv, av, vr]);
  const genTypical = getTypicalAgeScore("generalizationTotal", ageRange);
  const genAgeRange = getGeneralizationAgeRange(genTotal);
  const genDiff = getDifferenceInScore(genTypical, genTotal);

  // ── EQ subscales ──────────────────────────────────────────────────────
  const eqTotal = sumScores([
    toNum(d, "e_reflexivity_score"),
    toNum(d, "e_symmetry_score"),
    toNum(d, "e_transitivity_score"),
    toNum(d, "e_equivalence_score"),
  ]);

  // ── T subscales ───────────────────────────────────────────────────────
  const tTotal = sumScores([
    toNum(d, "t_non_arbitrary_level1_score"),
    toNum(d, "t_cultural_level2_score"),
    toNum(d, "t_arbitrary_level3_score"),
    toNum(d, "t_complex_level4_score"),
  ]);

  const derived: Partial<AssessmentData> = {
    // DT subscale derived
    dt_foundational_learning_typical_age_score: flTypical,
    dt_foundational_learning_age_range_equivalent: flAgeRange,
    dt_foundational_learning_difference_in_score: flDiff,
    dt_perceptual_learning_typical_age_score: plTypical,
    dt_perceptual_learning_age_range_equivalent: plAgeRange,
    dt_perceptual_learning_difference_in_score: plDiff,
    dt_verbal_comprehension_typical_age_score: vcTypical,
    dt_verbal_comprehension_age_range_equivalent: vcAgeRange,
    dt_verbal_comprehension_difference_in_score: vcDiff,
    dt_verbal_reasoning_memory_typical_age_score: vrmTypical,
    dt_verbal_reasoning_memory_age_range_equivalent: vrmAgeRange,
    dt_verbal_reasoning_memory_difference_in_score: vrmDiff,
    // DT module totals
    dt_total_score: dtTotal,
    dt_total_typical_age_score: dtTypical,
    dt_age_range_equivalent: dtAgeRange,
    dt_total_difference_in_score: dtDiff,
    // GEN subscale derived
    g_foundational_learning_basic_social_typical_age_score: flBsTypical,
    g_foundational_learning_basic_social_age_range_equivalent: flBsAgeRange,
    g_foundational_learning_basic_social_difference_in_score: flBsDiff,
    g_basic_verbal_comprehension_advanced_social_typical_age_score: bvTypical,
    g_basic_verbal_comprehension_advanced_social_difference_in_score: bvDiff,
    g_advanced_verbal_problem_solving_typical_age_score: avTypical,
    g_advanced_verbal_problem_solving_age_range_equivalent: avAgeRange,
    g_advanced_verbal_problem_solving_difference_in_score: avDiff,
    g_verbal_reasoning_advanced_typical_age_score: vrTypical,
    g_verbal_reasoning_advanced_age_range_equivalent: vrAgeRange,
    g_verbal_reasoning_advanced_difference_in_score: vrDiff,
    // GEN module totals
    g_total_score: genTotal,
    g_total_typical_age_score: genTypical,
    g_age_range_equivalent: genAgeRange,
    g_total_difference_in_score: genDiff,
    // EQ module total
    e_total_score: eqTotal,
    // T module total
    t_total_score: tTotal,
  };

  return { ...record, data: { ...record.data, ...derived } };
}

/**
 * Applies derived (auto-calculated) fields to assessment records that support them.
 * For VB-MAPP Milestones: computes `total_score` from subscale scores when missing.
 * For PEAK: computes module totals and age-based derived values (Typical Age Score,
 * Age Range Equivalent, Difference in Score) for all subscales and module totals.
 *
 * @param records - Normalized assessment records
 * @param clientAgeInYears - Client's current age in years (required for PEAK age-based fields)
 */
export function applyDerivedFields(
  records: AssessmentRecord[],
  clientAgeInYears: number | null
): AssessmentRecord[] {
  return records.map((record) => {
    const type = record.assessment_type ?? record.type;
    if (type === "vbmappMilestones") {
      return applyVbmappMilestoneDerived(record);
    }
    if (type === "peak") {
      return applyPeakDerived(record, clientAgeInYears);
    }
    return record;
  });
}
